import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import cx from "classnames";
import { useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useMedia } from "react-use";

import { usePoolsDetailsContext } from "context/PoolsDetailsContext/PoolsDetailsContext";
import {
  selectDepositMarketTokensData,
  selectGlvAndMarketsInfoData,
} from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { getTokenData } from "domain/synthetics/tokens";
import { getPageTitle } from "lib/legacy";
import { getByKey } from "lib/objects";
import { usePoolsIsMobilePage } from "pages/Pools/usePoolsIsMobilePage";

import Button from "components/Button/Button";
import ButtonLink from "components/Button/ButtonLink";
import Loader from "components/Common/Loader";
import SEO from "components/Common/SEO";
import Footer from "components/Footer/Footer";
import { GmSwapBox, GmSwapBoxProps } from "components/Synthetics/GmSwap/GmSwapBox/GmSwapBox";
import { GmSwapBoxHeader } from "components/Synthetics/GmSwap/GmSwapBox/GmSwapBoxHeader";
import { useCompositionData } from "components/Synthetics/MarketStats/hooks/useCompositionData";
import { MarketComposition } from "components/Synthetics/MarketStats/MarketComposition";
import { MarketGraphs } from "components/Synthetics/MarketStats/MarketGraphs";
import { Curtain } from "components/Synthetics/TradeBox/Curtain";

import { PoolsDetailsAbout } from "./PoolsDetailsAbout";
import { PoolsDetailsCard } from "./PoolsDetailsCard";
import { PoolsDetailsHeader } from "./PoolsDetailsHeader";

export function PoolsDetails() {
  useLingui();
  const glvAndMarketsInfoData = useSelector(selectGlvAndMarketsInfoData);

  const depositMarketTokensData = useSelector(selectDepositMarketTokensData);

  const { operation, mode, glvOrMarketAddress, setOperation, setMode, setGlvOrMarketAddress } =
    usePoolsDetailsContext();

  const [selectedMarketForGlv, setSelectedMarketForGlv] = useState<string | undefined>(undefined);

  const glvOrMarketInfo = getByKey(glvAndMarketsInfoData, glvOrMarketAddress);

  const marketToken = getTokenData(depositMarketTokensData, glvOrMarketAddress);

  const { backing: backingComposition, market: marketComposition } = useCompositionData({
    glvOrMarketInfo: glvOrMarketInfo,
    glvAndMarketsInfoData: glvAndMarketsInfoData,
    marketTokensData: depositMarketTokensData,
  });

  const isMobile = usePoolsIsMobilePage();

  const isInCurtain = useMedia("(max-width: 1180px)");

  return (
    <SEO title={getPageTitle(t`V2 Pools`)}>
      <div
        className={cx("default-container page-layout flex !max-w-[1048px] flex-col", {
          "gap-12": isMobile,
          "gap-16": !isMobile,
        })}
      >
        <Button variant="tertiary" className="h-32 w-max min-w-[67px]" size="small" to="/pools">
          <Trans>Back</Trans>
        </Button>
        {glvOrMarketInfo ? (
          <>
            <div className={cx("mb-15 flex justify-between gap-12", { "flex-wrap": isInCurtain })}>
              <div className="flex grow flex-col gap-16">
                <PoolsDetailsHeader glvOrMarketInfo={glvOrMarketInfo} marketToken={marketToken} />
                {glvOrMarketInfo && <MarketGraphs glvOrMarketInfo={glvOrMarketInfo} />}
                <PoolsDetailsCard title={<Trans>Composition</Trans>} childrenContainerClassName="!p-0">
                  <div className={cx("grid", { "grid-cols-1": isMobile, "grid-cols-2": !isMobile })}>
                    <div className={cx("border-gray-neutral", { "border-r pr-21": !isMobile, "border-b": isMobile })}>
                      <MarketComposition
                        type="backing"
                        label={<Trans>Backing Composition</Trans>}
                        title={<Trans>Exposure to Backing Tokens</Trans>}
                        composition={backingComposition}
                      />
                    </div>
                    <div className={cx({ "pl-21": !isMobile })}>
                      <MarketComposition
                        type="market"
                        label={<Trans>Market Composition</Trans>}
                        title={<Trans>Exposure to Market Traders’ PnL</Trans>}
                        composition={marketComposition}
                      />
                    </div>
                  </div>
                </PoolsDetailsCard>
              </div>

              <div
                className={cx("flex w-full flex-col gap-14", {
                  "mb-48 max-w-none": isInCurtain,
                  "max-w-[33.5rem]": !isInCurtain,
                })}
              >
                <PoolsDetailsGmSwapBox
                  selectedGlvOrMarketAddress={glvOrMarketAddress}
                  onSelectGlvOrMarket={setGlvOrMarketAddress}
                  selectedMarketForGlv={selectedMarketForGlv}
                  onSelectedMarketForGlv={setSelectedMarketForGlv}
                  operation={operation}
                  mode={mode}
                  onSetMode={setMode}
                  onSetOperation={setOperation}
                  isInCurtain={isInCurtain}
                />
                <PoolsDetailsCard className="!px-20 !py-26" title={<Trans>About</Trans>}>
                  <PoolsDetailsAbout
                    glvOrMarketInfo={glvOrMarketInfo}
                    marketToken={marketToken}
                    marketsInfoData={glvAndMarketsInfoData}
                    marketTokensData={depositMarketTokensData}
                  />
                </PoolsDetailsCard>
              </div>
            </div>
          </>
        ) : (
          <Loader />
        )}
        <Footer />
      </div>
    </SEO>
  );
}

const PoolsDetailsGmSwapBox = (props: GmSwapBoxProps & { isInCurtain: boolean }) => {
  const { isInCurtain } = props;

  if (!isInCurtain) {
    return (
      <div
        className={cx("rounded-8 bg-gray-darker", {
          "w-full max-w-[44rem]": !isInCurtain,
          "w-full max-w-none": isInCurtain,
        })}
      >
        <GmSwapBoxHeader {...props} />
        <GmSwapBox {...props} />
      </div>
    );
  }

  return (
    <Curtain header={<GmSwapBoxHeader {...props} isInCurtain />}>
      <GmSwapBox {...props} />
    </Curtain>
  );
};
